import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { WeekStartPicker } from "./WeekStartPicker";
import { errMsg, isWeekStart } from "./kpiFormat";

const DAYS = [
  ["grace_days", "Renewal grace (days)", "After a pack or membership ends, how long before it counts as churn"],
  ["default_purchase_window_days", "Lead purchase window (days)", "How long a new lead has to buy, unless an offer says otherwise"],
  ["default_intro_days", "Intro length (days)", "Used when an intro product has no length or expiry"],
  ["default_rollover_days", "Rollover window (days)", "Days after intro expiry to still count as a rollover"],
  ["episode_gap_days", "New lead episode gap (days)", "Repeat enquiries within this gap are the same lead"],
  ["established_attendance_days", "Existing client look-back (days)", "Leads who attended within this period aren't new leads"],
];
const TARGETS = [
  ["sales_conversion_pct", "Sales conversion target %"], ["rollover_pct", "Rollover target %"],
  ["monthly_churn_pct", "Monthly churn target % (lower is better)"], ["avg_visits_per_member", "Visits per member target"],
];
const SIGNOFF = [
  ["churn_month", "Churn month", "Churn is counted when the 30-day grace expires (not when the last credit ends)."],
  ["conversion_window", "Lead conversion window", "Conversion % = leads whose offer purchase window ended this week and who bought within it."],
  ["sales_value_scope", "Sales value scope", "Sales value = intro + qualifying pack + new recurring sales to eligible new leads, ex GST."],
  ["rollover_deadline", "Rollover deadline", "An intro client rolls over if they buy a membership or qualifying pack by expiry + rollover window."],
];
const CHANNELS = ["meta_lead_ad", "facebook_message", "instagram_message", "website", "phone", "email", "walkin", "sms", "referral", "other"];

const NumField = ({ id, label, hint, value, onChange, disabled }) => (
  <div className="space-y-1">
    <Label htmlFor={`kpi-set-${id}`} className="text-xs">{label}</Label>
    <Input id={`kpi-set-${id}`} type="number" min={0} value={value ?? ""} disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="h-9 font-mono-num" data-testid={`kpi-set-${id}`} />
    {hint && <p className="text-2xs text-zinc-500">{hint}</p>}
  </div>
);

const ServiceTargets = ({ services, value, main, onChange, disabled }) => (
  <div className="space-y-2" data-testid="kpi-service-targets">
    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Visits per member target by service</div>
    <p className="text-2xs text-zinc-500">Leave a box blank to use the main target ({main ?? "—"}). Each service's visits per member turns green or red against its target.</p>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {services.map((s) => (
        <div key={s.id} className="space-y-1">
          <Label htmlFor={`kpi-svc-target-${s.id}`} className="text-xs">{s.name}</Label>
          <Input id={`kpi-svc-target-${s.id}`} type="number" min={0} step="0.5" value={value?.[s.id] ?? ""} placeholder={main != null ? String(main) : ""}
            disabled={disabled} className="h-9 font-mono-num" data-testid={`kpi-svc-target-${s.id}`}
            onChange={(e) => onChange({ ...value, [s.id]: e.target.value === "" ? null : Number(e.target.value) })} />
        </div>
      ))}
    </div>
  </div>
);

export const RulesPanel = ({ settings, services = [], canEdit, onSaved }) => {
  const [s, setS] = useState(settings);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (s.dashboard_start_week && !isWeekStart(s.dashboard_start_week)) return toast.error("The export start week must be a Saturday");
    setBusy(true);
    try {
      const changes = { ...s };
      delete changes.updated_at;
      onSaved((await api.put("/kpi/settings", { changes })).data);
      toast.success("Report rules saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-6" data-testid="kpi-rules-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Rules, targets & sign-off</h3>
          <p className="text-sm text-zinc-500">Changes apply to new drafts only — approved weeks keep the figures they were approved with.</p>
        </div>
        {canEdit && <Button size="sm" onClick={save} disabled={busy} data-testid="kpi-rules-save-btn">Save rules</Button>}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="kpi-set-studio" className="text-xs">Studio name (spreadsheet)</Label>
          <Input id="kpi-set-studio" value={s.studio_name || ""} onChange={(e) => set("studio_name", e.target.value)} disabled={!canEdit} data-testid="kpi-set-studio" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Export starts on (Saturday)</Label>
          <WeekStartPicker value={s.dashboard_start_week || null} onChange={(v) => set("dashboard_start_week", v)}
            disabled={!canEdit} testId="kpi-set-start-week" placeholder="Last 52 weeks" clearable />
        </div>
        <div className="flex flex-col justify-end gap-2 text-sm">
          <label className="flex items-center gap-2"><Switch checked={!!s.prices_include_gst} onCheckedChange={(v) => set("prices_include_gst", v)} disabled={!canEdit} data-testid="kpi-set-gst" />Prices include 10% GST</label>
          <label className="flex items-center gap-2"><Switch checked={!!s.notify_email} onCheckedChange={(v) => set("notify_email", v)} disabled={!canEdit} data-testid="kpi-set-notify-email" />Email the weekly draft to Directors & Managers</label>
        </div>
        {DAYS.map(([k, l, h]) => <NumField key={k} id={k} label={l} hint={h} value={s[k]} onChange={(v) => set(k, v)} disabled={!canEdit} />)}
        {TARGETS.map(([k, l]) => <NumField key={k} id={k} label={l} value={s.targets?.[k]} disabled={!canEdit}
          onChange={(v) => set("targets", { ...s.targets, [k]: v })} />)}
      </div>
      <ServiceTargets services={services} value={s.service_visit_targets} main={s.targets?.avg_visits_per_member} disabled={!canEdit}
        onChange={(v) => set("service_visit_targets", v)} />
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Paid Meta lead channels</div>
        <div className="flex flex-wrap gap-3 text-sm">
          {CHANNELS.map((c) => (
            <label key={c} className="flex items-center gap-1.5">
              <Checkbox checked={(s.paid_channels || []).includes(c)} disabled={!canEdit} data-testid={`kpi-set-paid-${c}`}
                onCheckedChange={(v) => set("paid_channels", v ? [...(s.paid_channels || []), c] : (s.paid_channels || []).filter((x) => x !== c))} />
              {c.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Open decisions (spec §12) — provisional until signed off</div>
        {SIGNOFF.map(([k, l, h]) => (
          <label key={k} className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 text-sm">
            <Switch checked={!!s.rule_signoff?.[k]} disabled={!canEdit} data-testid={`kpi-signoff-${k}`}
              onCheckedChange={(v) => set("rule_signoff", { ...s.rule_signoff, [k]: v })} />
            <span><b>{l}</b> — {s.rule_signoff?.[k] ? "signed off" : "provisional (recommended rule in use)"}<br />
              <span className="text-xs text-zinc-500">{h}</span></span>
          </label>
        ))}
      </div>
    </section>
  );
};
